import { faFilePdf, faPencilSquare, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Button, Col, Container, Form, Row, Stack, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Confirm, { ConfirmInfo } from "../confirmComponent/confirm";
import { DataHandler } from "../dataHandler";
import LogoHeader from "../logoHeader/logoHeader";
import ModalAlert from "../sharedComponents/modalAlert";
import { HydroSpinner } from "../spinner/hydroSpinner";
import { benutzerVon, Buchung, formatStunden, heute, MeInfo, monatsErster, summeMinuten } from "./zeitData";
import ZeitEdit from "./zeitEdit";

type Gruppe = {
    user: string,
    name: string,
    zeiten: Array<Buchung>
}

/**
 * Gebuchte Zeiten im Datumsbereich. Benutzer aus zeitEditors sehen alle
 * Benutzer und dürfen bearbeiten, löschen und das PDF erzeugen, alle
 * anderen sehen nur ihre eigenen Zeiten.
 */
export default function ZeitListe() {

    const [me, setMe] = useState<MeInfo | null | undefined>();
    const [von, setVon] = useState<string>(monatsErster());
    const [bis, setBis] = useState<string>(heute());
    const [userFilter, setUserFilter] = useState<string>('');
    const [zeiten, setZeiten] = useState<Array<Buchung> | undefined>();
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();
    const [confirmInfo, setConfirmInfo] = useState<ConfirmInfo>();
    const [edit, setEdit] = useState<{ buchung?: Buchung } | undefined>();

    const navigate = useNavigate();

    /** Abgelaufene Session führt zurück zum Login, alles andere in den Fehlerdialog. */
    const zeigeFehler = useCallback((text: string) => {
        if (text === DataHandler.SESSION_EXPIRED)
            navigate('/login');
        else
            setError(text);
    }, [navigate]);

    const laden = useCallback(() => {
        setBusy(true);
        DataHandler.getZeitList(von, bis).then((data: Array<Buchung>) => {
            setZeiten(data);
        }).catch((text: string) => {
            setZeiten([]);
            zeigeFehler(text);
        }).finally(() => {
            setBusy(false);
        });
    }, [von, bis, zeigeFehler]);

    useEffect(() => {
        DataHandler.getMe().then((data: MeInfo) => {
            setMe(data);
        }).catch((text: string) => {
            setMe(null);
            zeigeFehler(text);
        });
    }, [zeigeFehler]);

    useEffect(() => {
        laden();
    }, [laden]);

    if (me === undefined || zeiten === undefined)
        return (<HydroSpinner />);

    if (me === null)
        return (<Container>
            <LogoHeader />
            {error && <ModalAlert callback={() => setError(undefined)} content={error} contentTitle="Fehler" variant="danger"></ModalAlert>}
            <Container>
                <h3>Konnte Daten nicht laden.</h3>
                <Button variant="secondary" onClick={() => navigate('/zeit')}>Zurück</Button>
            </Container>
        </Container>);

    const canEdit = me.canEditZeiten;
    const benutzer = benutzerVon(zeiten);
    // Der gewählte Benutzer bleibt wählbar, auch wenn er im Zeitraum keine Zeiten hat.
    const auswahl = userFilter.length > 0 && benutzer.indexOf(userFilter) === -1
        ? benutzer.concat([userFilter]) : benutzer;
    const gefiltert = userFilter.length > 0 ? zeiten.filter((z) => z.user === userFilter) : zeiten;

    const gruppen: Array<Gruppe> = [];
    for (let z of gefiltert) {
        let gruppe = gruppen.find((g) => g.user === z.user);
        if (!gruppe) {
            gruppe = { user: z.user, name: z.name, zeiten: [] };
            gruppen.push(gruppe);
        }
        gruppe.zeiten.push(z);
    }

    const vorSpalten = (canEdit ? 1 : 0) + 3;
    const nachSpalten = 1 + (canEdit ? 1 : 0);

    const remove = (buchung: Buchung) => {
        setConfirmInfo({
            text: 'Buchung vom ' + buchung.tagFormatiert + ' (' + buchung.name + ') wirklich löschen?',
            onConfirmCallback: () => {
                setConfirmInfo(undefined);
                setBusy(true);
                DataHandler.deleteZeit(buchung.id, von, bis).then((data: Array<Buchung>) => {
                    setZeiten(data);
                }).catch(zeigeFehler).finally(() => {
                    setBusy(false);
                });
            },
            onAbortCallback: () => {
                setConfirmInfo(undefined);
            }
        });
    }

    const createPdf = () => {
        setBusy(true);
        DataHandler.createZeitPdf(von, bis, userFilter.length > 0 ? userFilter : undefined)
            .catch(zeigeFehler).finally(() => {
                setBusy(false);
            });
    }

    const back = () => {
        navigate('/zeit');
    }

    return (
        <Container>
            <Confirm confirmInfo={confirmInfo} />
            {error && <ModalAlert callback={() => setError(undefined)} content={error} contentTitle="Fehler" variant="danger"></ModalAlert>}
            {edit && <ZeitEdit
                buchung={edit.buchung}
                defaultUser={userFilter.length > 0 ? userFilter : me.user}
                von={von}
                bis={bis}
                onSaved={(data: Array<Buchung>) => { setEdit(undefined); setZeiten(data); }}
                onAbort={() => setEdit(undefined)}
                onError={(text: string) => { setEdit(undefined); zeigeFehler(text); }} />}
            <LogoHeader />
            <h4>{canEdit ? 'Gebuchte Zeiten' : 'Meine Zeiten'}</h4>

            <Form>
                <Row className="align-items-end mb-3">
                    <Col xs={12} md={3}>
                        <Form.Group controlId="zeitVon">
                            <Form.Label>Von</Form.Label>
                            <Form.Control type="date" value={von} onChange={(e) => setVon(e.target.value)} />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Group controlId="zeitBis">
                            <Form.Label>Bis</Form.Label>
                            <Form.Control type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
                        </Form.Group>
                    </Col>
                    {canEdit && <Col xs={12} md={3}>
                        <Form.Group controlId="zeitBenutzer">
                            <Form.Label>Benutzer</Form.Label>
                            <Form.Select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
                                <option value=''>Alle Benutzer</option>
                                {auswahl.map((u) => (<option key={u} value={u}>{u}</option>))}
                            </Form.Select>
                        </Form.Group>
                    </Col>}
                </Row>
            </Form>

            {gefiltert.length === 0
                ? <div className="text-muted mb-3">Keine Zeiten in diesem Zeitraum.</div>
                : <Table striped bordered hover size="md">
                    <thead>
                        <tr>
                            {canEdit && <th>Benutzer</th>}
                            <th>Tag</th>
                            <th>Kommen</th>
                            <th>Gehen</th>
                            <th className="text-end">Stunden</th>
                            <th>Kommentar</th>
                            {canEdit && <th>Aktion</th>}
                        </tr>
                    </thead>
                    {gruppen.map((gruppe) => (
                        <tbody key={gruppe.user}>
                            {gruppe.zeiten.map((z) => (
                                <tr key={z.id} className={z.offen ? 'table-warning' : ''}>
                                    {canEdit && <td>{z.name}</td>}
                                    <td>{z.tagFormatiert}</td>
                                    <td>{z.kommenZeit}</td>
                                    <td>{z.offen ? 'offen' : z.gehenZeit}</td>
                                    <td className="text-end">{z.offen ? '' : z.stundenFormatiert}</td>
                                    <td>{z.kommentar}</td>
                                    {canEdit && <td>
                                        <Stack direction="horizontal" gap={1}>
                                            <Button onClick={() => setEdit({ buchung: z })} variant="secondary">
                                                <FontAwesomeIcon icon={faPencilSquare} className={"left-icon"} />
                                                &nbsp;Bearbeiten
                                            </Button>
                                            <Button onClick={() => remove(z)} variant="danger">
                                                <FontAwesomeIcon icon={faTimes} />
                                                &nbsp;Löschen
                                            </Button>
                                        </Stack>
                                    </td>}
                                </tr>
                            ))}
                            <tr>
                                <th colSpan={vorSpalten}>Summe {gruppe.name}</th>
                                <th className="text-end">{formatStunden(summeMinuten(gruppe.zeiten))}</th>
                                <th colSpan={nachSpalten}></th>
                            </tr>
                        </tbody>
                    ))}
                    {gruppen.length > 1 && <tbody>
                        <tr className="table-dark">
                            <th colSpan={vorSpalten}>Gesamtsumme</th>
                            <th className="text-end">{formatStunden(summeMinuten(gefiltert))}</th>
                            <th colSpan={nachSpalten}></th>
                        </tr>
                    </tbody>}
                </Table>}

            <Stack direction="horizontal" gap={3}>
                <Button variant="primary" disabled={busy} onClick={laden}>Neu laden</Button>
                {canEdit && <Button variant="success" disabled={busy} onClick={() => setEdit({})}>
                    <FontAwesomeIcon icon={faPlus} className={"left-icon"} />
                    &nbsp;Neue Buchung
                </Button>}
                {canEdit && <Button variant="info" disabled={busy} onClick={createPdf}>
                    <FontAwesomeIcon icon={faFilePdf} className={"left-icon"} />
                    &nbsp;Zeitenübersicht erstellen
                </Button>}
                <Button variant="secondary" onClick={back}>Zurück</Button>
            </Stack>
        </Container>
    );
}
